import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import AgentIntegration from "../../components/v2/agent-integration";
import Accordion from "../../components/v2/accordion/accordion";
import ProductResources from "../../components/v2/product/product-resource";
import ProductHeroSection from "../../components/v2/common/product-hero";
import ProductTour from "../../components/v2/product/product-tour";
import '../../styles/scss/_faq-section.module.scss';
import '../../styles/scss/_faq-section.module.scss';
import { APM, OpsAI, RUM } from "../../components/v2/icons-v2";
import ProductOverviewSection from "../../components/v2/product-overview-section";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";

export default function SyntheticMonitoring() {
    const { t } = useTranslation("common");

    const featuresData = t('product.synthetic-monitoring.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.synthetic-monitoring.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.synthetic-monitoring.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const productTourData = t('product.synthetic-monitoring.productTour', { returnObjects: true }) as any

    const agentIntegrationData = t('product.synthetic-monitoring.agentIntegration', { returnObjects: true }) as any

    const agentDeployData = t('product.shared.syntheticAgentDeploy', { returnObjects: true }) as any[]
    const agentDeployArray = Array.isArray(agentDeployData) ? agentDeployData : []

    const agentDeploy = agentDeployArray.map(agent => ({
        ...agent,
        icon: <></> // Synthetic monitoring uses empty icons
    }))

    const productOverviewData = {
        title: "Product overview",
        buttons: [
            {
                label: "View Docs",
                link: "https://docs.middleware.io/workflow/synthetic-monitoring/synthetic-monitoring",
                className: "ahref-btn primary",
            },
            {
                label: "View Pricing",
                link: "/pricing/",
                className: "ahref-btn secondary",
            },
        ],
        description:
            "Middleware Synthetic Monitoring is a proactive, code-free solution that simulates real user flows across APIs, browsers, and mobile to catch issues before customers are impacted. Run multi-step tests on schedules, chain API requests, apply assertions, and monitor endpoints from global locations. Detailed timing data and real-time alerts help your team jump-start investigations the moment an issue occurs, consistently delivering reliable user experiences.",
        worksWellWithTitle: "Works Well With",
        worksWellWith: [
            {
                title: "Browser",
                link: "/product/browser-testing/",
                icon: <OpsAI />
            },
            {
                title: "APM",
                link: "/product/apm/",
                icon: <APM />
            },
            {
                title: "RUM",
                link: "/product/real-user-monitoring/",
                icon: <RUM />
            },
        ],
    };

    return (
        <>
            <ProductHeroSection title={<h1>{t('product.synthetic-monitoring.hero.title')} <span>{t('product.synthetic-monitoring.hero.titleHighlight')}</span></h1>}
                description={t('product.synthetic-monitoring.hero.description')}
                img={"/images/v2/products/synthetic-monitoring.webp"} imgMobile={"/images/v2/products/synthetic-monitoring-mobile.webp"} imgAlt={t('product.synthetic-monitoring.hero.imageAlt')} preloadImg hasActions={true} tips={t('product.synthetic-monitoring.hero.tips')} />


            <ProductOverviewSection productOverviewData={productOverviewData} />


            <FeaturesMonitoringV2 productFeatures={features} tourId={"synthetics-tour"} />

            <CallToActionDocsLink
                title={'Need Help Setting Up Synthetic Tests?'}
                docsLink="https://docs.middleware.io/workflow/synthetic-monitoring/synthetic-monitoring"
            />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <ProductTour tourSrc={"https://app.supademo.com/embed/clzi9mdd61ko99x77hi8cv1g9?embed_v=2"} title={productTourData.title} tourId={"synthetic-tour"} description={productTourData.description} titleChanged={<h2>Take an <span className="text-gradient">Interactive Tour </span> of Synthetic Monitoring Tool</h2>} />

            <AgentIntegration
                agentDeploy={agentDeploy}
                title={
                    <h2>
                        <Trans
                            i18nKey="product.synthetic-monitoring.agentIntegration.title"
                            components={{ highlight: <span className="text-gradient" /> }}
                        />
                    </h2>
                }
                description={"Launch synthetic checks across HTTP, SSL, DNS, WebSocket, and gRPC with no setup overhead, no agents. Get instant visibility into uptime and performance."}
                contactUsVariant="synthetic-monitoring"
            />

            <ProductResources resources={resource} title={t('product.shared.resources.title')} />

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.synthetic-monitoring.faq.title')}</h2>
                    <p>{t('product.synthetic-monitoring.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true} />
                    </div>
                </div>
            </section>

        </>
    )
}